import { createExamApi } from "@/api/services/admin/learning-content";
import { Form } from "@/components/elements/Form";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { Button } from "@/components/elements/Button";
import { MenuNavigate } from "@/components/elements/MenuNavigate";
import { BasicInfoForm } from "@/features/learning-content/components/ExamForm/BasicInfoForm";
import { RelatedLinksForm } from "@/features/learning-content/components/ExamForm/RelatedLinksForm";
import { ProceduresForm } from "@/features/learning-content/components/ExamForm/ProceduresForm";
import { examSchema } from "@/features/learning-content/schema/ExamSchema";
import { TExamCreateForm } from "@/features/learning-content/types/learningContent";
import { convertExamFormToCreateReq } from "@/features/learning-content/utils/converts";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { AppRoutes } from "@/routes/config";
import { useToast } from "@/hooks/useToast";
import { setErrorForm, showError } from "@/utils/error";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { forwardRef, useImperativeHandle, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getDynamicRoute } from "@/utils/url";

type TExamFormProps = {
  industryName?: string;
  /** Pre-populated form data copied from a previous exam record */
  initialData?: TExamCreateForm;
  /** Lock exam method to CBT when the latest record already uses CBT */
  disableExamMethod?: boolean;
};

const initFormData: TExamCreateForm = {
  periodName: "",
  status: "active",
  examMethod: "",
  examMonth: null,
  examOverview: "",
  examInfoSource: "",
  internalMemo: "",
  relatedLinks: [{ label: "", url: "" }],
  procedures: [{ agent: "", site: null, stepName: "", description: "", duration: null, deadline: null }],
  examDayDescription: "",
  passFailDescription: "",
  announcementEstimate: "",
};

export const ExamForm = forwardRef<{ submit: () => void }, TExamFormProps>(({ industryName, initialData, disableExamMethod }, ref) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);

  const methods = useForm<TExamCreateForm>({
    defaultValues: initialData ?? initFormData,
    resolver: yupResolver(examSchema) as never,
    mode: "all",
    reValidateMode: "onBlur",
  });

  const onSubmit = async (data: TExamCreateForm) => {
    if (!id) return;

    setLoading(true);
    try {
      const payload = convertExamFormToCreateReq(data, Number(id));
      const rs = await createExamApi(payload);
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.LEARNING_CONTENT.FETCH_DETAIL, id] });
      navigate(getDynamicRoute(AppRoutes.industryDetail, { id }));
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
    setLoading(false);
  };

  useImperativeHandle(ref, () => ({
    submit: async () => {
      await methods.handleSubmit(onSubmit)();
    },
  }));

  return (
    <Form methods={methods} onSubmit={onSubmit} schema={examSchema} initialValues={initialData ?? initFormData}>
      <MenuNavigate
        menuPrefix={
          <Box sx={{ p: "16px 16px 12px 16px" }}>
            <Typography variant="body14" sx={{ fontWeight: 700, color: "text.primary", fontSize: "14px" }}>
              {industryName ?? "分野名"}
            </Typography>
          </Box>
        }
        contents={[
          {
            title: "基本情報",
            content: (
              <Stack gap={3}>
                <BasicInfoForm disableExamMethod={disableExamMethod} />
                <RelatedLinksForm />
              </Stack>
            ),
          },
          {
            title: "受験手続き情報",
            content: <ProceduresForm />,
          },
        ]}
        contentWidth="900px"
        contentAffix={
          <FooterContainer>
            <Stack gap={1} direction="row" justifyContent="space-between" alignItems="center">
              <Button
                size="sm-round"
                variant="secondary"
                onClick={() => {
                  if (id) {
                    navigate(getDynamicRoute(AppRoutes.industryDetail, { id }));
                  } else {
                    navigate(AppRoutes.learningContent);
                  }
                }}
              >
                戻る
              </Button>
              <Button disabled={loading} size="sm-round" type="submit">
                保存
              </Button>
            </Stack>
          </FooterContainer>
        }
      />
    </Form>
  );
});

ExamForm.displayName = "ExamForm";
